import { Link } from 'react-router-dom';

const SAMPLE_RECORDS = [
  { county: 'Los Angeles County, CA', parcels: '1,247', updated: 'Apr 1, 2026', status: 'Current' },
  { county: 'Clark County, NV',       parcels: '834',   updated: 'Mar 28, 2026', status: 'Current' },
  { county: 'Maricopa County, AZ',    parcels: '1,102', updated: 'Mar 30, 2026', status: 'Current' },
  { county: 'Sacramento County, CA',  parcels: '428',   updated: 'Mar 25, 2026', status: 'Current' },
  { county: 'Washoe County, NV',      parcels: '311',   updated: 'Mar 22, 2026', status: 'Current' },
  { county: 'Pima County, AZ',        parcels: '567',   updated: 'Mar 29, 2026', status: 'Current' },
];

export default function TaxRecordsContent() {
  return (
    <div className="flex flex-col gap-6 min-w-0">
      {/* Header */}
      <div>
        <h1 className="font-playfair text-3xl font-bold text-white">Tax Records</h1>
        <p className="text-white/40 text-sm mt-1">
          Delinquent tax parcel data across all monitored counties
        </p>
      </div>

      {/* Search bar */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
          <input
            type="text"
            placeholder="Search by county, parcel number, or address…"
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors"
          />
        </div>
        <button className="px-5 py-3 bg-gold text-navy-dark text-sm font-bold rounded-xl hover:bg-gold-light transition-colors whitespace-nowrap cursor-pointer">
          Search
        </button>
      </div>

      {/* Records table */}
      <div className="bg-white/4 border border-white/8 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
          <h2 className="text-white font-semibold text-sm">County Databases</h2>
          <span className="text-white/30 text-xs">{SAMPLE_RECORDS.length} counties indexed</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[540px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left py-3 px-6 text-white/30 text-xs font-semibold uppercase tracking-wide">County</th>
                <th className="text-left py-3 px-4 text-white/30 text-xs font-semibold uppercase tracking-wide">Parcels</th>
                <th className="text-left py-3 px-4 text-white/30 text-xs font-semibold uppercase tracking-wide">Last Updated</th>
                <th className="text-left py-3 px-4 text-white/30 text-xs font-semibold uppercase tracking-wide">Status</th>
                <th className="py-3 px-6" />
              </tr>
            </thead>
            <tbody>
              {SAMPLE_RECORDS.map((rec, i) => {
                const stateCode = rec.county.split(', ')[1] as 'CA' | 'NV' | 'AZ';
                const statePill: Record<string, string> = {
                  CA: 'bg-amber-500/15 text-amber-400',
                  NV: 'bg-red-500/15 text-red-400',
                  AZ: 'bg-orange-500/15 text-orange-400',
                };
                return (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                    <td className="py-3.5 px-6">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${statePill[stateCode]}`}>
                          {stateCode}
                        </span>
                        <span className="text-white/70 text-sm">{rec.county}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-white/50 text-sm">{rec.parcels}</td>
                    <td className="py-3.5 px-4 text-white/50 text-sm">{rec.updated}</td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                        {rec.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-6 text-right">
                      <Link
                        to="/property-listings"
                        className="text-xs text-gold/70 font-semibold hover:text-gold transition-colors whitespace-nowrap"
                      >
                        View Records →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* BDA external links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'CA Tax Sale Records', state: 'CA', href: 'https://businessdevelopmentauthority.net/ca-tax-foreclosures', pill: 'bg-amber-500/15 text-amber-400' },
          { label: 'NV Tax Sale Records', state: 'NV', href: 'https://businessdevelopmentauthority.net/nv-tax-foreclosures', pill: 'bg-red-500/15 text-red-400' },
          { label: 'AZ Tax Sale Records', state: 'AZ', href: 'https://businessdevelopmentauthority.net/tax-foreclosures', pill: 'bg-orange-500/15 text-orange-400' },
        ].map((item) => (
          <a
            key={item.state}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-4 bg-white/4 border border-white/8 rounded-xl hover:border-white/20 transition-colors group cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${item.pill}`}>{item.state}</span>
              <span className="text-white/70 text-sm font-medium group-hover:text-white transition-colors">{item.label}</span>
            </div>
            <i className="ri-external-link-line text-white/25 group-hover:text-white/60 transition-colors text-sm" />
          </a>
        ))}
      </div>
    </div>
  );
}
